import { useState, useEffect } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { STORIES } from "@/utils/storiesData";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const CeritaInteraktif = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [story, setStory] = useState(null);
  const [nodeId, setNodeId] = useState("start");
  const [history, setHistory] = useState([]);
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    if (!story) return;
    const n = story.nodes[nodeId];
    if (n && soundOn) speakText(n.text);
  }, [nodeId, story, soundOn]);

  const choose = (next) => {
    setHistory([...history, nodeId]);
    setNodeId(next);
  };

  const resetStory = () => { setNodeId("start"); setHistory([]); };

  const onEnding = (type) => {
    if (type === "good") recordGameWon("cerita");
  };

  // Menu: pick a story
  if (!story) {
    return (
      <div className="quiz-container flex flex-col items-center pt-3" data-testid="cerita-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-3xl p-6 w-full text-center mb-4">
          <motion.span className="text-7xl block mb-3" animate={{ rotate: [0,5,-5,0] }} transition={{ duration: 3, repeat: Infinity }}>📖</motion.span>
          <h1 className="font-fredoka text-4xl text-gold font-bold mb-2">Cerita Pilih</h1>
          <p className="font-fredoka text-gold-light/80 mb-4" style={{ fontSize: "clamp(0.9rem, 2vw, 1.2rem)" }}>Pilih cerita rakyat Nusantara! Setiap pilihanmu mengubah jalan cerita.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mb-4" data-testid="cerita-list">
          {STORIES.map(s => (
            <motion.button key={s.id} data-testid={`cerita-${s.id}`}
              onClick={() => { setStory(s); setNodeId("start"); setHistory([]); }}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className={`glass-card-strong rounded-2xl p-5 text-left bg-gradient-to-br ${s.color} bg-opacity-20 hover:shadow-2xl transition-all`}>
              <span className="text-5xl block mb-2">{s.emoji}</span>
              <h3 className="font-fredoka text-xl text-white font-bold mb-1">{s.title}</h3>
              <p className="font-fredoka text-white/80 text-sm">{s.desc}</p>
            </motion.button>
          ))}
        </div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl h-11"><Home className="w-4 h-4 mr-2" />Menu Utama</Button>
      </div>
    );
  }

  const node = story.nodes[nodeId];
  const isEnding = !node.choices;

  if (isEnding) {
    return (
      <div className="quiz-container flex flex-col items-center justify-center min-h-[80vh]" data-testid="cerita-ending">
        {node.ending === "good" && <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />}
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-3xl p-7 text-center w-full">
          <motion.span className="block mb-3" style={{ fontSize: "clamp(3rem, 8vw, 5rem)" }} animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>{node.emoji}</motion.span>
          <h2 className="font-fredoka font-bold mb-3" style={{ fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: node.ending === "good" ? "#FFD700" : node.ending === "bad" ? "#F87171" : "#A1A1AA" }}>
            {node.ending === "good" ? "🌟 Akhir Bahagia!" : node.ending === "bad" ? "😔 Akhir Sedih" : "💭 Akhir Biasa"}
          </h2>
          <p className="font-fredoka text-white mb-3" style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)" }}>{node.text}</p>
          <div className="bg-gold/10 border-2 border-gold/30 rounded-2xl p-4 mb-4">
            <p className="font-fredoka text-gold font-bold" style={{ fontSize: "clamp(0.95rem, 2.2vw, 1.25rem)" }}>💡 Pesan Moral: {node.message}</p>
          </div>
          {!history.includes("__recorded__") && node.ending === "good" && (() => { onEnding(node.ending); history.push("__recorded__"); return null; })()}
          <div className="flex flex-col gap-2">
            <Button data-testid="cerita-retry" onClick={resetStory} className="w-full font-fredoka font-bold rounded-xl h-11 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Ulang Cerita Ini</Button>
            <Button data-testid="cerita-change" onClick={() => setStory(null)} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl h-11"><BookOpen className="w-4 h-4 mr-2" />Cerita Lain</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl h-11"><Home className="w-4 h-4 mr-2" />Menu Utama</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="quiz-container flex flex-col items-center pt-3" data-testid="cerita-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">📖 {story.title}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setStory(null)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2 text-xs">Cerita Lain</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={nodeId} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
          className="glass-card-strong rounded-3xl p-6 w-full text-center mb-4">
          <motion.span className="block mb-3" style={{ fontSize: "clamp(3rem, 9vw, 6rem)" }}>{node.emoji}</motion.span>
          <p className="font-fredoka text-white mb-2" style={{ fontSize: "clamp(1.1rem, 3vw, 1.6rem)", lineHeight: 1.4 }}>{node.text}</p>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-1 gap-3 w-full" data-testid="cerita-choices">
        {node.choices.map((c, i) => (
          <motion.button key={`${nodeId}-${i}`} data-testid={`cerita-choice-${i}`}
            onClick={() => choose(c.next)}
            whileHover={{ scale: 1.02, x: 5 }} whileTap={{ scale: 0.98 }}
            className="ingredient-3d quiz-option-btn text-left text-slate-800 font-fredoka font-bold flex items-center justify-between">
            <span className="flex-1">{c.label}</span>
            <span className="text-2xl ml-3">➡️</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default CeritaInteraktif;
